package com.qrkontaq.ui.tenant

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.PublicMenuItemDto
import com.qrkontaq.data.dto.formatPrice
import com.qrkontaq.data.dto.localized
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

private data class TagStyle(val iconSvg: String?, val iconColor: String)

private val TAG_STYLES =
    mapOf(
        "vegan" to TagStyle(MenuIcons.Leaf, "#2E9E5B"),
        "spicy" to TagStyle(MenuIcons.Flame, "#E25822"),
        "new" to TagStyle(MenuIcons.Sparkles, "var(--qk-accent)"),
    )

private fun allergenLabel(code: String, lang: String): String = menuT(lang).allergen(code)

@Composable
private fun TagChip(tag: String, lang: String) {
    val style = TAG_STYLES[tag]
    Row(
        Modifier
            .backgroundColor(Brand.WarmBackground)
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 3.px, leftRight = 8.px)
            .margin(right = 5.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        style?.iconSvg?.let { icon ->
            Box(Modifier.margin(right = 4.px)) { InlineIcon(icon, 11, style.iconColor) }
        }
        SpanText(
            menuT(lang).tag(tag),
            Modifier.fontSize(11.px).fontWeight(FontWeight.Medium).color(Brand.TextSecondary),
        )
    }
}

@Composable
internal fun CategorySection(
    id: String,
    index: Int,
    title: String,
    items: List<PublicMenuItemDto>,
    lang: String,
    currency: String,
    accent: Color,
    featureFirst: Boolean,
    onItemClick: (PublicMenuItemDto) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 16.px)
            .id(id)
            .attrsModifier { classes("qk-section") },
    ) {
        // Editorial header: ghost index number + serif title, baseline-aligned.
        Row(
            Modifier.fillMaxWidth().margin(top = 30.px, bottom = 14.px),
            verticalAlignment = Alignment.Bottom,
        ) {
            SpanText(
                (index + 1).toString().padStart(2, '0'),
                Modifier
                    .fontSize(38.px)
                    .fontWeight(FontWeight.Bold)
                    .margin(right = 10.px)
                    .styleModifier { property("line-height", "0.82") }
                    .attrsModifier { classes("qk-ghost") },
            )
            SpanText(
                title,
                Modifier
                    .fontSize(23.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal)
                    .styleModifier { property("line-height", "0.95") }
                    .attrsModifier { classes("qk-display") },
            )
        }

        // Owner setting: the first photogenic dish becomes a full-width feature card.
        val feature =
            if (featureFirst) items.firstOrNull { it.photoUrl != null && it.isAvailable } else null
        feature?.let { FeatureDishCard(it, lang, currency, onItemClick) }
        items.filter { it !== feature }.forEachIndexed { i, item ->
            DishCard(item, lang, currency, accent, onItemClick, riseDelayMs = (i + 1) * 45)
        }
    }
}

@Composable
internal fun FeatureDishCard(
    item: PublicMenuItemDto,
    lang: String,
    currency: String,
    onClick: (PublicMenuItemDto) -> Unit,
) {
    Box(
        Modifier
            .fillMaxWidth()
            .height(190.px)
            .borderRadius(20.px)
            .margin(bottom = 12.px)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .boxShadow(offsetY = 6.px, blurRadius = 20.px, color = Color.rgba(31, 36, 33, 0.12f))
            .attrsModifier {
                classes("qk-card", "qk-rise")
                attr("role", "button")
                attr("tabindex", "0")
            }
            .onClick { onClick(item) },
    ) {
        Image(
            src = item.photoUrl!!,
            modifier = Modifier
                .fillMaxSize()
                .objectFit(ObjectFit.Cover)
                .attrsModifier {
                    attr("alt", item.name.localized(lang))
                    attr("loading", "lazy")
                },
        )
        Box(Modifier.fillMaxSize().attrsModifier { classes("qk-feature-grad") })

        if (item.likes > 0) {
            Row(
                Modifier
                    .position(Position.Absolute)
                    .top(10.px)
                    .right(10.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(topBottom = 3.px, leftRight = 9.px)
                    .attrsModifier { classes("qk-glass") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(MenuIcons.HeartFilled, 11, "#FF8A7A")
                SpanText(
                    "${item.likes}",
                    Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface).margin(left = 4.px),
                )
            }
        }

        Row(
            Modifier
                .position(Position.Absolute)
                .left(0.px).right(0.px).bottom(0.px)
                .padding(14.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(Modifier.fillMaxWidth().margin(right = 10.px)) {
                SpanText(
                    item.name.localized(lang),
                    Modifier
                        .fontSize(20.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Surface)
                        .styleModifier {
                            property("line-height", "1.2")
                            property("text-shadow", "0 1px 12px rgba(0,0,0,.4)")
                        }
                        .attrsModifier { classes("qk-display") },
                )
                val meta = listOfNotNull(
                    item.weight,
                    item.calories?.let { menuT(lang).kcal(it) },
                ).joinToString(" · ")
                if (meta.isNotEmpty()) {
                    SpanText(
                        meta,
                        Modifier.fontSize(12.px).color(Color.rgba(255, 255, 255, 0.85f)).margin(top = 3.px),
                    )
                }
            }
            Box(
                Modifier
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(topBottom = 6.px, leftRight = 13.px)
                    // Long dish names squeeze this flex item, pushing the nowrap
                    // price past the pill's right padding — never shrink it.
                    .flexShrink(0)
                    .attrsModifier { classes("qk-glass-strong") },
            ) {
                SpanText(
                    formatPrice(item.priceMinor, currency),
                    Modifier
                        .fontSize(15.px)
                        .fontWeight(FontWeight.Bold)
                        .styleModifier { property("color", "var(--qk-accent)") }
                        .attrsModifier { classes("qk-nowrap") },
                )
            }
        }
    }
}

@Composable
internal fun DishCard(
    item: PublicMenuItemDto,
    lang: String,
    currency: String,
    accent: Color,
    onClick: (PublicMenuItemDto) -> Unit,
    riseDelayMs: Int = 0,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(Brand.RadiusCard.px)
            .margin(bottom = 10.px)
            .padding(12.px)
            .cursor(Cursor.Pointer)
            .opacity(if (item.isAvailable) 1.0 else 0.55)
            .boxShadow(offsetY = 1.px, blurRadius = 4.px, color = Color.rgba(31, 36, 33, 0.05f))
            .styleModifier {
                if (riseDelayMs > 0) property("animation-delay", "${riseDelayMs}ms")
            }
            .attrsModifier {
                classes("qk-card", "qk-rise")
                attr("role", "button")
                attr("tabindex", "0")
            }
            .onClick { onClick(item) },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.fillMaxWidth()) {
            SpanText(
                item.name.localized(lang),
                Modifier
                    .fontSize(15.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .styleModifier { property("line-height", "1.3") },
            )

            val description = item.description.localized(lang)
            if (description.isNotEmpty()) {
                SpanText(
                    description,
                    Modifier
                        .fontSize(13.px)
                        .color(Brand.TextSecondary)
                        .margin(top = 3.px)
                        .styleModifier { property("line-height", "1.45") }
                        .attrsModifier { classes("qk-clamp2") },
                )
            }

            Row(
                Modifier.fillMaxWidth().margin(top = 8.px).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (!item.isAvailable) {
                    UnavailableBadge(lang)
                } else {
                    item.tags.take(3).forEach { tag -> TagChip(tag, lang) }
                }
                val meta = listOfNotNull(
                    item.weight,
                    item.calories?.let { menuT(lang).kcal(it) },
                ).joinToString(" · ")
                if (meta.isNotEmpty()) {
                    SpanText(meta, Modifier.fontSize(12.px).color(Brand.TextSecondary))
                }
            }

            Row(
                Modifier.fillMaxWidth().margin(top = 8.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    formatPrice(item.priceMinor, currency),
                    Modifier
                        .fontSize(16.px)
                        .fontWeight(FontWeight.Bold)
                        .color(accent)
                        .attrsModifier { classes("qk-nowrap") },
                )
                if (item.likes > 0) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        InlineIcon(MenuIcons.HeartFilled, 12, "#FF6B5B")
                        SpanText(
                            "${item.likes}",
                            Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(left = 4.px),
                        )
                    }
                }
            }
        }

        item.photoUrl?.let { photo ->
            Box(
                Modifier
                    .size(92.px)
                    .minWidth(92.px)
                    .margin(left = 12.px),
            ) {
                Image(
                    src = photo,
                    modifier = Modifier
                        .fillMaxSize()
                        .borderRadius(14.px)
                        .objectFit(ObjectFit.Cover)
                        .styleModifier {
                            if (!item.isAvailable) property("filter", "grayscale(1)")
                        }
                        .attrsModifier {
                            attr("alt", item.name.localized(lang))
                            attr("loading", "lazy")
                        },
                )
            }
        }
    }
}

@Composable
private fun UnavailableBadge(lang: String) {
    SpanText(
        menuT(lang).outOfStock,
        Modifier
            .fontSize(11.px)
            .fontWeight(FontWeight.SemiBold)
            .color(Brand.Coral)
            .backgroundColor(Brand.CoralSoft)
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 3.px, leftRight = 8.px)
            .margin(right = 5.px),
    )
}

@Composable
internal fun ShowcaseRow(
    title: String,
    iconSvg: String,
    items: List<PublicMenuItemDto>,
    lang: String,
    currency: String,
    accent: Color,
    onItemClick: (PublicMenuItemDto) -> Unit,
) {
    Column(Modifier.fillMaxWidth().margin(bottom = 6.px)) {
        Row(
            Modifier.margin(left = 16.px, top = 20.px, bottom = 10.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(iconSvg, 18, "var(--qk-accent)")
            SpanText(
                title,
                Modifier
                    .fontSize(19.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal)
                    .margin(left = 8.px)
                    .attrsModifier { classes("qk-display") },
            )
        }
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 12.px)
                .overflow { x(Overflow.Auto) }
                .styleModifier { property("white-space", "nowrap") }
                .attrsModifier { classes("qk-hide-scrollbar", "qk-snap-x") },
        ) {
            items.forEach { item ->
                Column(
                    Modifier
                        .margin(leftRight = 4.px)
                        .minWidth(148.px)
                        .maxWidth(148.px)
                        .backgroundColor(Brand.Surface)
                        .borderRadius(Brand.RadiusCard.px)
                        .padding(8.px)
                        .cursor(Cursor.Pointer)
                        .attrsModifier {
                            classes("qk-card", "qk-snap-item")
                            attr("role", "button")
                            attr("tabindex", "0")
                        }
                        .onClick { onItemClick(item) },
                ) {
                    Box(Modifier.fillMaxWidth().height(100.px).position(Position.Relative)) {
                        item.photoUrl?.let { photo ->
                            Image(
                                src = photo,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .borderRadius(11.px)
                                    .objectFit(ObjectFit.Cover)
                                    .attrsModifier {
                                        attr("alt", item.name.localized(lang))
                                        attr("loading", "lazy")
                                    },
                            )
                        } ?: Box(
                            Modifier.fillMaxSize().borderRadius(11.px).backgroundColor(Brand.WarmBackground),
                            contentAlignment = Alignment.Center,
                        ) {
                            InlineIcon(MenuIcons.Utensils, 24, "#C9C3BA")
                        }
                        if (item.likes > 0) {
                            Row(
                                Modifier
                                    .position(Position.Absolute)
                                    .top(6.px)
                                    .right(6.px)
                                    .borderRadius(Brand.RadiusChip.px)
                                    .padding(topBottom = 2.px, leftRight = 7.px)
                                    .styleModifier {
                                        property("background", "rgba(255,255,255,.92)")
                                        property("backdrop-filter", "blur(6px)")
                                    },
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                InlineIcon(MenuIcons.HeartFilled, 10, "#FF6B5B")
                                SpanText(
                                    "${item.likes}",
                                    Modifier
                                        .fontSize(11.px)
                                        .fontWeight(FontWeight.SemiBold)
                                        .color(Brand.Charcoal)
                                        .margin(left = 3.px)
                                        // The badge pill stays white over the photo in dark mode.
                                        .attrsModifier { classes("qk-ink") },
                                )
                            }
                        }
                    }
                    SpanText(
                        item.name.localized(lang),
                        Modifier
                            .fontSize(13.px)
                            .fontWeight(FontWeight.Medium)
                            .color(Brand.Charcoal)
                            .margin(top = 7.px)
                            .styleModifier {
                                property("line-height", "1.3")
                                property("white-space", "normal")
                            }
                            .attrsModifier { classes("qk-clamp2") },
                    )
                    SpanText(
                        formatPrice(item.priceMinor, currency),
                        Modifier
                            .fontSize(14.px)
                            .fontWeight(FontWeight.Bold)
                            .color(accent)
                            .margin(top = 4.px)
                            .attrsModifier { classes("qk-nowrap") },
                    )
                }
            }
        }
    }
}

@Composable
internal fun DishSheet(
    dish: PublicMenuItemDto,
    lang: String,
    currency: String,
    accent: Color,
    onClose: () -> Unit,
    onLikesChanged: (itemId: String, likes: Long) -> Unit = { _, _ -> },
) {
    // Lock page scroll and close on Escape while the sheet is open.
    DisposableEffect(Unit) {
        val bodyStyle = document.body?.style
        val previousOverflow = bodyStyle?.getPropertyValue("overflow") ?: ""
        bodyStyle?.setProperty("overflow", "hidden")
        val keyListener: (Event) -> Unit = { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") onClose()
        }
        window.addEventListener("keydown", keyListener)
        onDispose {
            bodyStyle?.setProperty("overflow", previousOverflow)
            window.removeEventListener("keydown", keyListener)
        }
    }

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
            .zIndex(50)
            .attrsModifier { classes("qk-backdrop") }
            .onClick { onClose() },
    )

    Box(
        Modifier
            .position(Position.Fixed)
            .left(0.px).right(0.px).bottom(0.px)
            .zIndex(51),
        contentAlignment = Alignment.BottomCenter,
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(640.px)
                .backgroundColor(Brand.Surface)
                .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                .maxHeight(88.vh)
                .overflow { y(Overflow.Auto) }
                .attrsModifier {
                    classes("qk-sheet", "qk-hide-scrollbar")
                    attr("role", "dialog")
                    attr("aria-modal", "true")
                },
        ) {
            Box(Modifier.fillMaxWidth().position(Position.Relative)) {
                dish.photoUrl?.let { photo ->
                    Image(
                        src = photo,
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(240.px)
                            .objectFit(ObjectFit.Cover)
                            .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                            .attrsModifier { attr("alt", dish.name.localized(lang)) },
                    )
                }

                // Drag handle over the photo (or on the sheet surface when no photo).
                Box(
                    Modifier.fillMaxWidth().padding(top = 10.px),
                    contentAlignment = Alignment.TopCenter,
                ) {
                    Box(
                        Modifier
                            .width(40.px).height(4.px)
                            .borderRadius(2.px)
                            .then(
                                if (dish.photoUrl != null) {
                                    Modifier.styleModifier { property("background", "rgba(255,255,255,.75)") }
                                } else {
                                    Modifier.backgroundColor(Brand.Divider)
                                },
                            ),
                    )
                }

                Box(
                    Modifier
                        .position(Position.Absolute)
                        .top(12.px)
                        .right(12.px)
                        .size(36.px)
                        .borderRadius(Brand.RadiusChip.px)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("background", "rgba(255,255,255,.92)")
                            property("backdrop-filter", "blur(8px)")
                            property("box-shadow", "0 2px 8px rgba(31,36,33,.14)")
                        }
                        .attrsModifier {
                            classes("qk-press")
                            attr("role", "button")
                            attr("aria-label", menuT(lang).close)
                            attr("tabindex", "0")
                        }
                        .onClick { onClose() },
                    contentAlignment = Alignment.Center,
                ) {
                    // Ink stays dark on the white glass circle in dark mode too.
                    InlineIcon(MenuIcons.Close, 16, "#1F2421", extraClass = "qk-ink")
                }
            }

            Column(Modifier.fillMaxWidth().padding(left = 18.px, right = 18.px, top = 16.px, bottom = 18.px)) {
                SpanText(
                    dish.name.localized(lang),
                    Modifier
                        .fontSize(22.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal)
                        .styleModifier { property("line-height", "1.2") }
                        .attrsModifier { classes("qk-display") },
                )

                Row(
                    Modifier.fillMaxWidth().margin(top = 8.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        formatPrice(dish.priceMinor, currency),
                        Modifier
                            .fontSize(20.px)
                            .fontWeight(FontWeight.Bold)
                            .color(accent)
                            .attrsModifier { classes("qk-nowrap") },
                    )
                }

                DishActions(dish, lang, onLikesChanged)

                val description = dish.description.localized(lang)
                if (description.isNotEmpty()) {
                    SpanText(
                        description,
                        Modifier
                            .fontSize(14.px)
                            .color(Brand.TextSecondary)
                            .margin(top = 10.px)
                            .styleModifier { property("line-height", "1.55") },
                    )
                }

                Row(
                    Modifier.fillMaxWidth().margin(top = 12.px).flexWrap(FlexWrap.Wrap).gap(6.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    dish.weight?.let { MetaChip(it) }
                    dish.calories?.let { MetaChip(menuT(lang).kcal(it)) }
                    dish.tags.forEach { TagChip(it, lang) }
                }

                if (dish.allergens.isNotEmpty()) {
                    Row(
                        Modifier.margin(top = 14.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        InlineIcon(MenuIcons.Info, 13, "#6B7280")
                        SpanText(
                            menuT(lang).allergensPrefix +
                                dish.allergens.joinToString(", ") { allergenLabel(it, lang) },
                            Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(left = 6.px),
                        )
                    }
                }

                Box(
                    Modifier
                        .fillMaxWidth()
                        .margin(top = 18.px)
                        .backgroundColor(accent)
                        .borderRadius(14.px)
                        .padding(topBottom = 13.px)
                        .cursor(Cursor.Pointer)
                        .attrsModifier {
                            classes("qk-press")
                            attr("role", "button")
                            attr("tabindex", "0")
                        }
                        .onClick { onClose() },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        menuT(lang).close,
                        Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface),
                    )
                }
            }
        }
    }
}

/** Like (any guest; the browser remembers its vote) and share actions inside the dish sheet. */
@Composable
private fun DishActions(
    dish: PublicMenuItemDto,
    lang: String,
    onLikesChanged: (itemId: String, likes: Long) -> Unit,
) {
    // The heart lights up instantly from the per-browser vote memory; the server
    // is the source of the count and dedups repeat votes from the same account.
    var liked by remember(dish.id) { mutableStateOf(dish.id in GuestSession.likedItemsLocally()) }
    var likeCount by remember(dish.id) { mutableStateOf(dish.likes) }
    var busy by remember(dish.id) { mutableStateOf(false) }

    // A signed-in account may also carry likes from other devices/sessions.
    androidx.compose.runtime.LaunchedEffect(dish.id) {
        if (GuestApi.restoreSessionQuietly() || GuestSession.isLoggedIn) {
            if (!liked && dish.id in GuestApi.likedItemIds()) {
                liked = true
                GuestSession.setLikedLocally(dish.id, true)
            }
        }
    }
    var shared by remember { mutableStateOf(false) }

    fun toggleLike() {
        if (busy) return
        busy = true
        MainScope().launch {
            val target = !liked
            if (GuestApi.likeItem(dish.id, target)) {
                liked = target
                // Clamp at zero: a stale local "liked" flag (e.g. the server lost
                // the vote) must never render a negative count.
                likeCount = maxOf(0L, likeCount + if (target) 1L else -1L)
                GuestSession.setLikedLocally(dish.id, target)
                onLikesChanged(dish.id, likeCount)
            }
            busy = false
        }
    }

    fun share() {
        val nav = window.navigator.asDynamic()
        if (nav.share != null) {
            val data = js("{}")
            data.title = dish.name.localized(lang)
            data.url = window.location.href
            nav.share(data)
        } else {
            window.navigator.clipboard.writeText(window.location.href)
            shared = true
        }
    }

    Row(Modifier.fillMaxWidth().margin(top = 12.px).gap(8.px), verticalAlignment = Alignment.CenterVertically) {
        // Like pill
        Row(
            Modifier
                .backgroundColor(Brand.WarmBackground)
                .borderRadius(Brand.RadiusChip.px)
                .padding(topBottom = 8.px, leftRight = 14.px)
                .cursor(Cursor.Pointer)
                .attrsModifier {
                    classes("qk-press")
                    attr("role", "button")
                    attr("aria-pressed", "$liked")
                }
                .onClick { toggleLike() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(
                if (liked) MenuIcons.HeartFilled else MenuIcons.HeartOutline,
                15,
                if (liked) "#E53935" else "#6B7280",
            )
            SpanText(
                "$likeCount",
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(left = 6.px),
            )
        }
        // Share pill
        Row(
            Modifier
                .backgroundColor(Brand.WarmBackground)
                .borderRadius(Brand.RadiusChip.px)
                .padding(topBottom = 8.px, leftRight = 14.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { share() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.Share, 14, "#6B7280")
            SpanText(
                when {
                    shared -> menuT(lang).linkCopied
                    else -> menuT(lang).share
                },
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(left = 6.px),
            )
        }
    }

}

@Composable
private fun MetaChip(text: String) {
    SpanText(
        text,
        Modifier
            .fontSize(12.px)
            .color(Brand.TextSecondary)
            .backgroundColor(Brand.WarmBackground)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 3.px, leftRight = 10.px),
    )
}
